// Schermata di chiamata a tutto schermo: squillo, connessione, timer, muto,
// riaggancia, esito. Solo presentazione: lo stato arriva da VoiceCallProvider.
import { Mic, MicOff, PhoneOff, X } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CALL_COPY, formatCallDuration, type CallErrorCode } from "./call-copy";
import type { CallPeer, CallPhase } from "./VoiceCallProvider";

interface CallScreenProps {
  phase: Exclude<CallPhase, "idle">;
  peer: CallPeer;
  elapsedSeconds: number;
  isMuted: boolean;
  isSpeaking: boolean;
  networkWarning: boolean;
  error: CallErrorCode | null;
  onToggleMute: () => void;
  onHangUp: () => void;
  onClose: () => void;
}

export function CallScreen({
  phase, peer, elapsedSeconds, isMuted, isSpeaking, networkWarning, error, onToggleMute, onHangUp, onClose,
}: CallScreenProps) {
  const statusLine =
    phase === "ringing" ? CALL_COPY.ringing
    : phase === "connecting" ? CALL_COPY.connecting
    : phase === "connected" ? formatCallDuration(elapsedSeconds)
    : error ? CALL_COPY.errors[error] : CALL_COPY.ended;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={peer.name}
      className="fixed inset-0 z-[200] flex flex-col items-center justify-between bg-gradient-to-b from-gray-900 via-gray-900 to-black px-4 pb-12 pt-20 text-white"
    >
      <div className="flex flex-col items-center gap-5 text-center">
        <div className="relative">
          {(phase === "ringing" || (phase === "connected" && isSpeaking)) && (
            <span className="absolute inset-0 rounded-full bg-pink-500/30 animate-ping" aria-hidden />
          )}
          <Avatar className="relative h-32 w-32 border-4 border-white/10">
            <AvatarImage src={peer.photoUrl || undefined} className="object-cover" />
            <AvatarFallback className="bg-pink-500 text-4xl text-white">
              {peer.name.charAt(0)}
            </AvatarFallback>
          </Avatar>
        </div>
        <h2 className="text-2xl font-semibold">{peer.name}</h2>
        <p className={`max-w-xs text-base ${error ? "text-red-300" : "text-white/70"} tabular-nums`}>
          {statusLine}
        </p>
        {networkWarning && phase === "connected" && (
          <p className="text-sm text-yellow-300">{CALL_COPY.networkWarning}</p>
        )}
      </div>

      {phase === "ended" ? (
        <button
          type="button"
          onClick={onClose}
          className="flex h-14 items-center gap-2 rounded-full bg-white/10 px-6 text-white hover:bg-white/20"
        >
          <X className="h-5 w-5" />
          {CALL_COPY.close}
        </button>
      ) : (
        <div className="flex items-center gap-10">
          <button
            type="button"
            onClick={onToggleMute}
            disabled={phase !== "connected"}
            aria-label={isMuted ? CALL_COPY.unmute : CALL_COPY.mute}
            aria-pressed={isMuted}
            className={`flex h-16 w-16 items-center justify-center rounded-full transition-colors disabled:opacity-40 ${
              isMuted ? "bg-white text-gray-900" : "bg-white/10 text-white hover:bg-white/20"
            }`}
          >
            {isMuted ? <MicOff className="h-7 w-7" /> : <Mic className="h-7 w-7" />}
          </button>
          <button
            type="button"
            onClick={onHangUp}
            aria-label={CALL_COPY.hangUp}
            className="flex h-16 w-16 items-center justify-center rounded-full bg-red-600 text-white hover:bg-red-700"
          >
            <PhoneOff className="h-7 w-7" />
          </button>
        </div>
      )}
    </div>
  );
}
